import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
  CSSProperties,
} from "react";
import cx from "classnames";

import { Button } from "../../Button";
import { useMergeRefs } from "../../../hooks";
import { SrOnly } from "../../SrOnly";

import {
  ANIMATION_DURATION,
  TOAST_STACK_PADDING,
  EXPAND_BUTTON_HEIGHT_WITH_PADDING,
  SCROLL_THRESHOLD,
  EVENT_THROTTLE_MS,
} from "./constants";
import { Toast, ToastProps } from "./Toast";
import { toast } from "../toast";
import { useHotkeyFocus } from "./useHotkeyFocus";
import { useShowMoreButtonPositionStyles } from "./useShowMoreButtonPositionStyles";
import { useToasts } from "./useToasts";

import styles from "./Toaster.module.scss";

/**
 * Props for the ToastStack component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type ToastStackProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> & {
  /**
   * Duration of the toast in milliseconds. If set to `false`, the toast will not auto-dismiss.
   * If `actions` are provided in the toast method, this prop will be ignored and treated as `false`.
   * @default false
   */
  duration?: number | false;
  /**
   * How a screen-reader will announce the hotkey used to set focus on the first toast.
   * @default "t"
   */
  focusKey?: string;
  /**
   * How many toasts to display before they start stacking.
   * @default 3
   */
  toastsBeforeStack?: number;
  /**
   * The toasts to display in the stack.
   * @default []
   */
  toasts: ToastProps[];
};

/**
 * ToastStack component for displaying a stack of toast notifications.
 *
 * Features:
 * - Displays multiple toasts in a vertical stack
 * - Automatic stacking behavior when toast count exceeds limit
 * - Show more button to expand stacked toasts
 * - Keyboard navigation with hotkey support
 * - Hover pause functionality for toast timers
 * - Smooth animations and transitions
 * - Accessibility features with screen reader support
 * - Responsive design with mobile touch support
 * - Focus management for keyboard users
 *
 * @example
 * <ToastStack
 *   toasts={toastList}
 *   duration={5000}
 *   toastsBeforeStack={3}
 *   focusKey="t"
 * />
 *
 * @example
 * <ToastStack
 *   toasts={[toast1, toast2]}
 *   duration={false}
 *   toastsBeforeStack={5}
 * />
 */
export const ToastStack = forwardRef<HTMLDivElement, ToastStackProps>(
  (
    {
      className,
      duration = false,
      focusKey = "t",
      toasts,
      toastsBeforeStack = 3,
      ...props
    },
    ref,
  ) => {
    const toastStackRef = useRef<HTMLDivElement>(null);
    const firstToastRef = useRef<HTMLDivElement | null>(null);
    const mergedToastStackRef = useMergeRefs([toastStackRef, ref]);
    const {
      setToastsBeforeStack,
      extraToastsVisible,
      setExtraToastsVisible,
      getToastStackHeight,
      isDialogToaster,
    } = useToasts();
    const toastCount = toasts.length;
    const isToastStackEmpty = toastCount === 0;

    const lastToastCount = useRef<number>(0);
    const resizeThrottleRef = useRef(false);
    const resizeTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const scrollThrottleRef = useRef(false);
    const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const [toastStackHeight, setToastStackHeight] = useState<number>(0);
    const [disableAnimations, setDisableAnimations] = useState(false);
    const [toastPausedStatus, setToastPausedStatus] =
      useState<ToastProps["pausedStatus"]>(undefined);
    const [showTopGradient, setShowTopGradient] = useState(false);
    const [showBottomGradient, setShowBottomGradient] = useState(true);
    // Initialize to 0 for SSR compatibility; set actual value in useEffect
    const [viewportHeight, setViewportHeight] = useState<number>(0);

    const isOverflowing =
      toastStackHeight + EXPAND_BUTTON_HEIGHT_WITH_PADDING > viewportHeight;

    // Update viewport height on window resize (throttled)
    useEffect(() => {
      // Set initial value on mount
      setViewportHeight(window.innerHeight - TOAST_STACK_PADDING);

      const handleResize = () => {
        if (resizeThrottleRef.current) return;

        resizeThrottleRef.current = true;
        resizeTimerRef.current = setTimeout(() => {
          setViewportHeight(window.innerHeight - TOAST_STACK_PADDING);
          resizeThrottleRef.current = false;
        }, EVENT_THROTTLE_MS);
      };

      window.addEventListener("resize", handleResize);
      return () => {
        clearTimeout(resizeTimerRef.current);
        window.removeEventListener("resize", handleResize);
      };
    }, []);

    useEffect(() => {
      if (toastCount === 0) {
        setToastPausedStatus(undefined);
      }
    }, [toastCount]);

    // Track scroll position to show/hide gradients (only when expanded)
    useEffect(() => {
      const toastStackElement = toastStackRef.current;

      // When collapsed with overflow, just show bottom gradient (no scroll tracking)
      if (!extraToastsVisible) {
        setShowTopGradient(false);
        setShowBottomGradient(isOverflowing);
        return;
      }

      if (!toastStackElement) {
        setShowTopGradient(false);
        setShowBottomGradient(true);
        return;
      }

      const updateGradients = () => {
        const { scrollTop, scrollHeight, clientHeight } = toastStackElement;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // Show top gradient when scrolled down from top
        setShowTopGradient(scrollTop > 0);
        // Show bottom gradient when not at bottom (within threshold)
        setShowBottomGradient(distanceFromBottom > SCROLL_THRESHOLD);
      };

      const handleScroll = () => {
        if (scrollThrottleRef.current) return;

        scrollThrottleRef.current = true;
        scrollTimerRef.current = setTimeout(() => {
          updateGradients();
          scrollThrottleRef.current = false;
        }, EVENT_THROTTLE_MS);
      };

      // Initial check (unthrottled)
      updateGradients();

      toastStackElement.addEventListener("scroll", handleScroll, {
        passive: true,
      });
      return () => {
        clearTimeout(scrollTimerRef.current);
        toastStackElement.removeEventListener("scroll", handleScroll);
      };
    }, [extraToastsVisible, isOverflowing]);

    useEffect(() => {
      const scrollListener = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toastStackRef.current?.scrollTo({
          top: toastStackRef.current.scrollTop + e.deltaY,
        });
      };

      if (isDialogToaster) {
        // prevent existing toasts from sliding in again
        setDisableAnimations(isDialogToaster);
        const animationTimer = setTimeout(() => {
          setDisableAnimations(false);
        }, 500);

        // fix scrolling for toast stacks in dialogs
        const toastStackRefCurrent = toastStackRef.current;
        toastStackRefCurrent?.addEventListener("wheel", scrollListener, {
          passive: false,
        });

        return () => {
          clearTimeout(animationTimer);
          toastStackRefCurrent?.removeEventListener("wheel", scrollListener);
        };
      }
    }, [isDialogToaster]);

    // reset toast stack height when toasts change
    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (
        toastCount > lastToastCount.current ||
        isDialogToaster ||
        extraToastsVisible
      ) {
        // Update immediately when adding toasts or expanding
        setToastStackHeight(getToastStackHeight());
      } else {
        // Delay update when removing toasts or collapsing to allow animation
        timer = setTimeout(
          () => setToastStackHeight(getToastStackHeight()),
          ANIMATION_DURATION - 10,
        );
      }
      lastToastCount.current = toastCount;
      return () => {
        clearTimeout(timer);
      };
      // only run after toastStackHeight updates or dialog state changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getToastStackHeight, isDialogToaster, extraToastsVisible]);

    useEffect(() => {
      setToastsBeforeStack(toastsBeforeStack);
    }, [setToastsBeforeStack, toastsBeforeStack]);

    // Collapse the toast stack (only called by collapse button)
    const handleCollapseStack = useCallback(() => {
      setExtraToastsVisible(false);
      setToastPausedStatus("resumed");

      if (toastStackRef.current?.scrollTo) {
        toastStackRef.current?.scrollTo(0, 0);
      }
    }, [setExtraToastsVisible]);

    const handleResumeToastTimers = useCallback(() => {
      setToastPausedStatus("resumed");
    }, []);

    const { returnFocusRef } = useHotkeyFocus({
      disabled: isToastStackEmpty,
      hotkey: focusKey,
      onHotkeyIn: async () => {
        setExtraToastsVisible(true);
        return new Promise((resolve) => {
          // add a delay for the ToastStack to render the hidden toasts
          setTimeout(() => {
            resolve();
          }, 100);
        });
      },
      onHotkeyOut: () => {
        handleResumeToastTimers();
      },
      refToFocus: firstToastRef as React.MutableRefObject<HTMLElement>,
      rootRef: toastStackRef as React.MutableRefObject<HTMLElement>,
    });

    const showMoreButtonPositionStyles = useShowMoreButtonPositionStyles();

    const showExpandCollapseButton = toastCount > toastsBeforeStack;
    const expandCollapseButtonText = extraToastsVisible
      ? "Collapse all"
      : "Expand all";

    // Use getToastStackHeight() directly to avoid stale state during expand animation
    const currentStackHeight = useMemo(
      () => getToastStackHeight(),
      [getToastStackHeight],
    );

    // Check if current stack would overflow (for button animation)
    // Include button space to trigger overflow if button would be off-screen at all
    const hasOverflow =
      currentStackHeight + EXPAND_BUTTON_HEIGHT_WITH_PADDING > viewportHeight;

    // Calculate button top position
    const expandCollapseButtonTop: CSSProperties["top"] = hasOverflow
      ? `calc(100dvh - 4.5rem)`
      : extraToastsVisible
        ? `calc(${currentStackHeight}px - 1rem)`
        : showMoreButtonPositionStyles.top;

    const toastStackClasses = cx(styles["toast-stack"], className, {
      [styles["show-more-button"]]:
        showExpandCollapseButton && !extraToastsVisible,
      [styles["show-extra-toast"]]: extraToastsVisible,
      [styles["disable-animations"]]: disableAnimations,
      [styles["has-overflow"]]: hasOverflow,
    });

    const handleToastStackFocusHover = () => {
      if (toastPausedStatus === "resumed" || toastPausedStatus === undefined) {
        setToastPausedStatus(Date.now());
      }
    };

    const handleToastStackOnBlur = () => {
      if (!toastStackRef.current?.matches(":focus-within")) {
        handleResumeToastTimers();
      }
    };

    const handleExpandCollapseClick = () => {
      if (extraToastsVisible) {
        handleCollapseStack();
      } else {
        setExtraToastsVisible(true);
      }
    };

    let toastStackHeightStyle: CSSProperties["height"] = 0;

    if (extraToastsVisible) {
      // need to reduce padding here due to box-sizing: content-box
      toastStackHeightStyle = "calc(100vh - var(--toast-stack-padding) * 2)";
    } else if (toastCount > toastsBeforeStack) {
      // 3rem to leave room for show more button
      toastStackHeightStyle = `calc(${toastStackHeight}px + 3rem + var(--toast-stack-padding))`;
    } else {
      toastStackHeightStyle = toastStackHeight;
    }

    return (
      <>
        <div
          data-anv="ToastStack"
          className={toastStackClasses}
          ref={mergedToastStackRef}
          onMouseOver={handleToastStackFocusHover}
          onMouseLeave={handleResumeToastTimers}
          onFocus={handleToastStackFocusHover}
          onBlur={handleToastStackOnBlur}
          style={{
            height: toastStackHeightStyle,
          }}
          {...props}
        >
          {toasts.map(({ toastClassName, onClose, ...toastProps }, index) => {
            const isStacked =
              !extraToastsVisible && index < toastCount - toastsBeforeStack;

            const isLastToastDisplayed =
              index === toastCount - toastsBeforeStack;

            const isHidden = index < toastCount - toastsBeforeStack - 2;

            const toastClasses = cx(styles.toast, toastClassName);
            const toastContainerClasses = cx(styles["toast-wrapper"], {
              [styles.stacked]: isStacked || isLastToastDisplayed,
              [styles["stack-one"]]:
                isStacked && index === toastCount - toastsBeforeStack - 1,
              [styles["stack-two"]]:
                isStacked && index === toastCount - toastsBeforeStack - 2,
              [styles["stacked-extra"]]: isHidden,
              [styles["stacked-show-extra"]]: extraToastsVisible,
            });

            const handleOnClose = (
              e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
            ) => {
              onClose?.(e);
              returnFocusRef.current?.focus();
              toast.dismiss(toastProps.id);
            };

            return (
              <Toast
                containerClassName={toastContainerClasses}
                duration={duration}
                index={index}
                isStacked={isStacked}
                isBelowStackIndex={index < toastCount - toastsBeforeStack}
                key={toastProps.id}
                onClose={handleOnClose}
                pausedStatus={toastPausedStatus}
                toastClassName={toastClasses}
                ref={index === 0 ? firstToastRef : undefined}
                {...toastProps}
              />
            );
          })}
          {showExpandCollapseButton && (
            <div
              className={cx(styles["show-more-button-container"], {
                [styles["fixed-position"]]: hasOverflow,
              })}
              style={{ top: expandCollapseButtonTop }}
            >
              <Button
                className={styles["show-collapse-button"]}
                onClick={handleExpandCollapseClick}
                appearance="secondary"
              >
                {expandCollapseButtonText}
              </Button>
            </div>
          )}
          {/* Spacer element to extend scroll area so last toast can scroll above the fixed button (only when expanded) */}
          {extraToastsVisible && hasOverflow && (
            <div
              className={styles["toast-stack-spacer"]}
              style={{ top: `calc(${currentStackHeight}px + 2rem)` }}
              aria-hidden="true"
            />
          )}
          <SrOnly aria-live="polite">
            New toast message appeared. Press the {focusKey} key to focus on the
            toast messages. Press escape to return focus to previous position.
          </SrOnly>
        </div>
        {hasOverflow && (
          <>
            <div
              className={cx(styles["toast-stack-gradient-top"], {
                [styles["toast-stack-gradient-visible"]]: showTopGradient,
              })}
              aria-hidden="true"
            />
            <div
              className={cx(styles["toast-stack-gradient-bottom"], {
                [styles["toast-stack-gradient-visible"]]: showBottomGradient,
              })}
              aria-hidden="true"
            />
          </>
        )}
      </>
    );
  },
);

ToastStack.displayName = "ToastStack";
